import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as LockPin } from 'assets/icons/LockPin.svg';
import useSecretStorageSecurityCode from 'modules/EndToEndEncryption/hooks/useSecretStorageSecurityCode';
import logger from 'utils/logging/faro';
import testingConstants from 'utils/testingConstants';
import { Schemas } from 'utils/yup/schemas/validationSchemas';

import SecurityCodeModalBase from '../CreateSecurityCodeModal/SecurityCodeModalBase';
import SecurityCodeModalFooter from '../CreateSecurityCodeModal/SecurityCodeModalFooter';
import { SECURITY_CODE_LENGTH } from '../securityCodeConstants';
import SecurityCodeInput, { SecurityCodeInputHandle } from '../SecurityCodeInput/SecurityCodeInput';

interface EnterCurrentSecurityCodeStepProps {
  close: () => void;
  onConfirmed: () => void;
}

const EnterCurrentSecurityCodeStep = ({ close, onConfirmed }: EnterCurrentSecurityCodeStepProps) => {
  const { t } = useTranslation();
  const { unlockWithSecurityCode, isUnlocking, errorKey, clearError } = useSecretStorageSecurityCode();
  const [securityCode, setSecurityCode] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const [isShaking, setIsShaking] = useState(false);
  const passcodeRef = useRef<SecurityCodeInputHandle>(null);

  useEffect(() => {
    if (errorKey) setIsShaking(true);
  }, [errorKey]);

  const displayError = localError || (errorKey ? t(errorKey) : null);

  const handleSubmit = async () => {
    try {
      Schemas.getEnterSecurityCodeValidation().validateSync({ securityCode });
    } catch (validationError) {
      if (validationError instanceof Error) {
        setLocalError(validationError.message);
        logger.warn('EnterCurrentSecurityCodeStep: validation failed', validationError.message);
      }
      setSecurityCode('');
      passcodeRef.current?.focusFirst();
      return;
    }

    try {
      await unlockWithSecurityCode(securityCode);
      onConfirmed();
    } catch (err) {
      logger.warn('EnterCurrentSecurityCodeStep: unlock failed', err instanceof Error ? err.message : String(err));
      setSecurityCode('');
      passcodeRef.current?.focusFirst();
    }
  };

  const handleChange = (newValue: string) => {
    setSecurityCode(newValue);
    if (localError) setLocalError(null);
    if (errorKey) clearError();
  };

  return (
    <div data-testid={testingConstants.changeSecurityCodeModal}>
      <SecurityCodeModalBase
        isVisible
        onClose={close}
        icon={LockPin}
        title={t('verification.change_security_code_enter_current_title')}
        footer={
          <SecurityCodeModalFooter
            onCancel={close}
            cancelLabel={t('cancel')}
            onConfirm={handleSubmit}
            confirmLabel={t('verification.change_security_code_confirm_cta')}
            isLoading={isUnlocking}
            isDisabled={isUnlocking || securityCode.length !== SECURITY_CODE_LENGTH}
            confirmTestId={testingConstants.changeSecurityCodeConfirmBtn}
          />
        }
      >
        <div data-testid={testingConstants.changeSecurityCodeCurrentInput}>
          <SecurityCodeInput
            ref={passcodeRef}
            value={securityCode}
            onChange={handleChange}
            onSubmit={handleSubmit}
            error={displayError}
            helperText={t('verification.change_security_code_enter_current_helper')}
            autoFocus
            disabled={isUnlocking}
            isShaking={isShaking}
            onShakeEnd={() => setIsShaking(false)}
          />
        </div>
      </SecurityCodeModalBase>
    </div>
  );
};

export default EnterCurrentSecurityCodeStep;
